/**
 * Tailwind class strings shared by both apps' ResponsiveSheet shells. Each
 * value is pre-composed so consumers concatenate (via cn()) but never edit —
 * any visual change is made here once and both apps pick it up.
 *
 * Apple-style close button comes from feedback (feedback_close_button_style)
 * and is the canonical default.
 */

export const sheetClasses = {
  // Backdrop ──────────────────────────────────────────────────────────────
  // Dim + blur — shared by all ResponsiveSheets across both apps. Bumped from
  // /30 + blur-sm to /45 + blur-md so login + filter + bottom-sheet modals
  // have the same pronounced glass effect as the design system reference.
  backdrop: "fixed inset-0 bg-black/45 backdrop-blur-md",

  // Mobile bottom-sheet container ─────────────────────────────────────────
  // Bottom safe-area is owned by the footer when present (its
  // `pb-[max(env,...)]` already clears the gesture bar). The shell hosting
  // this class (e.g. ResponsiveSheet) is responsible for adding
  // `pb-[env(safe-area-inset-bottom)]` only when the sheet renders with no
  // footer — keeping a single layer of safe-area in either case (PWA bug:
  // applying it on both the panel and the footer doubled the buffer ≈ 68px).
  bottomSheet:
    "fixed inset-x-0 bottom-0 flex max-h-[85dvh] flex-col overflow-hidden rounded-t-2xl border-t border-border bg-card shadow-2xl",

  // Desktop centered dialog ───────────────────────────────────────────────
  dialogPanel:
    "fixed left-1/2 top-1/2 flex max-h-[90vh] w-full -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-2xl",

  // Desktop side-sheet (right-anchored) ───────────────────────────────────
  sideSheet:
    "fixed inset-y-0 right-0 flex w-full max-w-md flex-col overflow-hidden border-l border-border bg-card shadow-2xl",

  // Desktop floating card (corner-anchored) ───────────────────────────────
  // Width/height are intentionally fixed — float-right cards are status /
  // feedback / chat heads, not content panels. Consumers tune corner offset
  // through panelClassName when they need to clear a FAB or sidebar.
  floatRight:
    "fixed inset-auto bottom-4 right-4 flex max-h-[min(85dvh,640px)] w-80 flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-2xl",

  // Width tiers (apply to dialogPanel / sideSheet variants) ───────────────
  widthSm: "max-w-sm",
  widthMd: "max-w-md",
  widthLg: "max-w-lg",
  widthXl: "max-w-xl",
  width2xl: "max-w-2xl",
  width3xl: "max-w-3xl",
  width4xl: "max-w-4xl",
  widthFull: "max-w-full",

  // Drag handle (mobile only) ─────────────────────────────────────────────
  handleWrap:
    "flex shrink-0 cursor-grab justify-center pt-2.5 pb-1.5 active:cursor-grabbing touch-pan-y select-none",
  handlePill: "h-1 w-10 rounded-full bg-muted-foreground/30",

  // Header (drag-handle + title + actions + close) ────────────────────────
  headerWrap: "flex shrink-0 items-center gap-2 border-b border-border px-4 pb-3",
  headerWrapNoHandle: "flex shrink-0 items-center gap-2 border-b border-border px-4 py-3",
  headerTitleColumn: "min-w-0 flex-1",
  headerTitle: "text-base leading-tight font-semibold text-foreground",
  headerDescription: "mt-0.5 text-xs text-muted-foreground",

  // Apple-style close button (X) ──────────────────────────────────────────
  closeButton:
    "inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-muted/70 text-foreground transition-colors hover:bg-muted",
  closeIcon: "size-4",

  // Body (scrollable region between header and footer) ────────────────────
  // Default content padding mirrors the header — px-4 on mobile, px-6 on
  // desktop — so children don't touch the panel edges. Consumers that need
  // edge-to-edge content (country picker list, image crop canvas) override
  // via `bodyClassName="p-0"`; tailwind-merge ensures the override wins.
  body: "min-h-0 flex-1 overflow-y-auto px-4 pt-3 pb-4 sm:px-6 sm:pt-4 sm:pb-6",

  // Footer (sticky CTA region) ────────────────────────────────────────────
  footer:
    "flex shrink-0 items-center justify-end gap-3 border-t border-border bg-card px-4 py-3 pb-[max(env(safe-area-inset-bottom),0.75rem)]",
} as const;

/**
 * Map a `width` prop value to its corresponding Tailwind class. Used by
 * dialog-mode shells (centered) and side-sheet shells.
 */
export function sheetWidthClass(
  width: "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl" | "full" | undefined,
): string {
  switch (width) {
    case "sm":
      return sheetClasses.widthSm;
    case "lg":
      return sheetClasses.widthLg;
    case "xl":
      return sheetClasses.widthXl;
    case "2xl":
      return sheetClasses.width2xl;
    case "3xl":
      return sheetClasses.width3xl;
    case "4xl":
      return sheetClasses.width4xl;
    case "full":
      return sheetClasses.widthFull;
    case "md":
    default:
      return sheetClasses.widthMd;
  }
}
